/**
 * <s-family-bar> — Sticky jump bar for color chart pattern families. Pins itself just
 * below the site header and marks the family currently on screen with aria-current.
 *
 * Child markup:
 *   a[href^="#family-"]        — One link per family section.
 */
class SFamilyBar extends HTMLElement {
  #abort = null;
  #links = [];
  #frame = 0;

  connectedCallback() {
    this.#links = Array.from(this.querySelectorAll('a[href^="#family-"]'));
    if (!this.#links.length) return;
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const update = () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#update());
    };
    window.addEventListener('scroll', update, { passive: true, signal });
    window.addEventListener('resize', update, { passive: true, signal });
    this.#update();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #update() {
    const header = document.querySelector('body > header, header');
    const headerHeight = header && getComputedStyle(header).position === 'sticky' ? header.getBoundingClientRect().height : 0;
    this.style.top = `${Math.round(headerHeight)}px`;

    const line = headerHeight + this.getBoundingClientRect().height + 48;
    let active = null;
    for (const link of this.#links) {
      const section = document.getElementById(link.hash.slice(1));
      if (!section || section.hidden) continue;
      if (section.getBoundingClientRect().top <= line) active = link;
    }
    const first = this.#links[0] && document.getElementById(this.#links[0].hash.slice(1));
    if (first && first.getBoundingClientRect().top > line) active = null;

    // The last families can be too short to ever reach the line; at the bottom of the
    // page, light up the last family that is on screen instead.
    const scroller = document.scrollingElement || document.documentElement;
    const atBottom = window.innerHeight + window.scrollY >= scroller.scrollHeight - 4;
    if (atBottom) {
      for (const link of this.#links) {
        const section = document.getElementById(link.hash.slice(1));
        if (!section || section.hidden) continue;
        const rect = section.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > line) active = link;
      }
    }

    this.#links.forEach((link) => {
      if (link === active) {
        if (link.getAttribute('aria-current') !== 'true') {
          link.setAttribute('aria-current', 'true');
          const row = link.parentElement;
          if (row && row.scrollWidth > row.clientWidth) {
            row.scrollTo({ left: link.offsetLeft - (row.clientWidth - link.offsetWidth) / 2, behavior: 'smooth' });
          }
        }
      } else {
        link.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('s-family-bar')) {
  customElements.define('s-family-bar', SFamilyBar);
}
